import React, { useState, useEffect } from 'react';
import { Nav, Navbar } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navigation.css';
import CompanySearch  from './CompanySearch';
import { logger }     from '../../utils/logger';
import defaultAvatar  from '../../assets/avatar/default_avatar.png';
import headerLogo     from '../../assets/images/header_logo.png';
import { useCompany } from '../context/CompanyContext';
import { useAuth }    from '../../features/user/auth/AuthContext';

import LoginDialog     from '../../features/user/auth/LoginDialog';
import RegisterDialog  from '../../features/user/auth/RegisterDialog';
import activityTracker from '../../features/user/ActivityTracker';
import { API_CONFIG, apiClient } from '../../api/config';
import { message, Dropdown, Avatar, Button }     from 'antd';
import { 
  AudioOutlined, 
  FileTextOutlined, 
  DollarOutlined, 
  BankOutlined, 
  FundOutlined, 
  BarChartOutlined,
  InfoCircleOutlined,
  StarOutlined,
  StarFilled,
  AppstoreOutlined,
  RocketOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyInfo, updateCompanyInfo } = useCompany();
  const [showParentNav, setShowParentNav] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isCheckingWatchlist, setIsCheckingWatchlist] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);


  
  // 判斷當前是否在 Report 相關頁面
  const isReportSection = ['/maininfo', '/transcript', '/list-10q10k', '/latest-10q10k', '/business-product','/income', '/balance', '/cashflow', '/keymetric', '/estimate','/pages/finance-gpt', '/technical/candlestick'].includes(location.pathname);
  const isFinancialSection = ['/income', '/balance', '/cashflow', '/keymetric', '/estimate'].includes(location.pathname);
  const isTechnicalSection = ['/technical/candlestick'].includes(location.pathname);

  
  // 處理 URL 參數中的 symbol 並獲取公司信息
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    if (urlSymbol && urlSymbol !== companyInfo?.symbol) {
      logger.log('Navigation: 從 URL 參數檢測到新的 symbol:', urlSymbol);
      
      // 調用 API 獲取公司信息
      const fetchCompanyInfo = async () => {
        try {
          const response = await apiClient.get(API_CONFIG.endpoints.companySearch, {
            params: { term: urlSymbol }
          });
          
          if (response.data && response.data.length > 0) {
            // 找到匹配的公司信息
            const companyData = response.data.find(company => 
              company.symbol === urlSymbol.toUpperCase()
            ) || response.data[0];
            
            logger.log('Navigation: 獲取到公司信息:', companyData);
            
            // 更新 CompanyContext
            updateCompanyInfo({
              symbol: companyData.symbol,
              company_name: companyData.company_name || '',
              sector: companyData.sector || '',
              industry: companyData.industry || '',
              image: companyData.image || ''
            });
            
            logger.log('Navigation: 已更新 CompanyContext');
          } else {
            logger.warn('Navigation: 未找到公司信息，使用基本數據');
            // 如果沒有找到公司信息，使用基本數據
            updateCompanyInfo({
              symbol: urlSymbol.toUpperCase(),
              company_name: '',
              sector: '',
              industry: '',
              image: ''
            });
          }
        } catch (error) {
          logger.error('Navigation: 獲取公司信息失敗:', error);
          // 發生錯誤時，使用基本數據
          updateCompanyInfo({
            symbol: urlSymbol.toUpperCase(),
            company_name: '',
            sector: '',
            industry: '',
            image: ''
          });
        }
      };
      
      fetchCompanyInfo();
    }
  }, [location.search, companyInfo?.symbol, updateCompanyInfo]);
  

  
  // 当路径变化时，隐藏父导航栏
  useEffect(() => {
    setShowParentNav(false);
  }, [location.pathname]);
  
  // 当路径变化时，检查是否需要更新导航
  useEffect(() => {
    // 如果当前在报告相关页面且有公司信息，则更新导航
    if (companyInfo?.symbol && (isReportSection || isFinancialSection || isTechnicalSection)) {
      // 根据当前页面决定导航行为
      if (location.pathname.includes('/maininfo')) {
        navigate('/maininfo', { replace: true });
      } else if (location.pathname.includes('/list-10q10k')) {
        navigate('/list-10q10k', { replace: true });
      } else if (location.pathname.includes('/latest-10q10k')) {
        navigate('/latest-10q10k', { replace: true });
      } else if (location.pathname.includes('/business-product')) {
        navigate('/business-product', { replace: true });
      } else if (location.pathname.includes('/transcript')) {
        navigate('/transcript', { replace: true });
      } else if (location.pathname.includes('/income')) {
        navigate('/income', { replace: true });
      } else if (location.pathname.includes('/balance')) {
        navigate('/balance', { replace: true });
      } else if (location.pathname.includes('/cashflow')) {
        navigate('/cashflow', { replace: true });
      } else if (location.pathname.includes('/keymetric')) {
        navigate('/keymetric', { replace: true });
      } else if (location.pathname.includes('/estimate')) {
        navigate('/estimate', { replace: true });
      } else if (location.pathname.includes('/technical/candlestick')) {
        navigate('/technical/candlestick', { replace: true });
      }
    }
  }, [location.pathname, companyInfo?.symbol, navigate, isReportSection, isFinancialSection, isTechnicalSection]);

  // 处理子导航链接点击
  const handleSubNavClick = (path) => {
    // 如果已经有选择的公司，则在导航时带上公司代码
    if (companyInfo?.symbol) {
      activityTracker.trackBtnExecution('navigation', companyInfo.symbol, path);
      navigate(path, { replace: true });
    } else {
      navigate(path);
    }
  };

  // 处理公司选择
  const handleCompanySelect = (selectedCompany) => {
    // 获取公司代码
    const selectedSymbol = selectedCompany?.symbol || selectedCompany;
    if (selectedSymbol) {
      // 如果是完整的公司信息对象，则更新 companyInfo
      if (typeof selectedCompany === 'object' && selectedCompany.company_name) {
        updateCompanyInfo(selectedCompany);
      }
      
      // 一律导航到资讯主页
      activityTracker.trackBtnExecution('company_select', selectedSymbol, '/maininfo');
      logger.log('導航到資訊主頁');
      navigate('/maininfo', { replace: true });
    }
  };

  const handleLogout = () => {
    logout();
    setAnchorEl(null);
    logger.log('用户手动登出');
  };

  // 添加状态变化记录
  useEffect(() => {
    logger.log('Navigation 组件检测到认证状态变化:', isAuthenticated);
    //logger.log('当前用户:', user);
  }, [isAuthenticated, user]);

  // 检查是否已收藏
  const checkWatchlist = async () => {
    if (!isAuthenticated || !user || !companyInfo?.symbol || (!isReportSection && !isFinancialSection && !isTechnicalSection)) return;
    
    try {
      setIsCheckingWatchlist(true);
      const response = await apiClient.get(
        `${API_CONFIG.endpoints.watchlist.check}?symbol=${companyInfo.symbol}&group=default&user_id=${user.user_id}`
      );
      setIsInWatchlist(response.data.isInWatchlist);
    } catch (error) {
      logger.error('检查收藏状态失败:', error);
    } finally {
      setIsCheckingWatchlist(false);
    }
  };

  // 添加收藏
  const handleAddToWatchlist = async () => {
    if (!isAuthenticated || !user) {
      message.warning('请先登录');
      return;
    }

    try {
      await apiClient.post(API_CONFIG.endpoints.watchlist.add, {
        symbol: companyInfo.symbol,
        group: 'default', // 默认添加到 default 分组
        user_id: user.user_id
      });
      setIsInWatchlist(true);
      activityTracker.trackBtnExecution('add_to_watchlist', companyInfo.symbol, location.pathname);
      message.success('已添加到收藏列表');
    } catch (error) {
      logger.error('添加收藏失败:', error);
      message.error(error.response?.data?.message || '添加收藏失败');
    }
  };

  // 当公司信息或用户状态变化时检查收藏状态
  useEffect(() => {
    checkWatchlist();
  }, [companyInfo?.symbol, isAuthenticated, user]);



  // 判斷是否顯示優惠橫幅
  const shouldShowPromoBanner = !isAuthenticated || !user?.access_tier || user?.access_tier === 'free';
  
  // 調試日誌
  useEffect(() => {
    if (isAuthenticated && user) {
      logger.log('Navigation: 用戶登入狀態檢查', {
        isAuthenticated,
        user_id: user.user_id,
        access_tier: user.access_tier,
        shouldShowPromoBanner
      });
    }
  }, [isAuthenticated, user, shouldShowPromoBanner]);

  return (
    <div className="navigation">
      {shouldShowPromoBanner && (
        <div className="promo-banner">
          <span>新註冊用戶 70% off 優惠 限量100名</span>
          <Button 
            type="primary" 
            size="small" 
            onClick={() => navigate('/prices')}
            className="promo-button"
          >
            馬上訂閱
          </Button>
        </div>
      )}
      {/* Parent Navbar */}
      <div className="parent-navbar">
        <div className="parent-navbar-container">
          <div className="navbar-logo">
            <Link to="/">
              <img src={headerLogo} alt="Logo" />
            </Link>
          </div>
          <div className="navbar-search-container">
            <CompanySearch onSelect={handleCompanySelect} />
          </div>
          
          <div className="auth-section">
            {isAuthenticated && user ? (
              <>
                <Link to="/watchlist" style={{ marginRight: '20px', color: '#ffcc00' }} title="收藏列表">
                  <StarOutlined style={{ fontSize: '20px' }} />
                </Link>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'profile',
                        label: (
                          <Link to="/profile" onClick={() => setAnchorEl(false)}>
                            個人資料
                          </Link>
                        )
                      },
                      {
                        key: 'watchlist',
                        label: (
                          <Link to="/watchlist" onClick={() => setAnchorEl(false)}>
                            自選股列表
                          </Link>
                        )
                      },
                      {
                        key: 'prices',
                        label: (
                          <Link to="/prices" onClick={() => setAnchorEl(false)}>
                            目前方案
                          </Link>
                        )
                      },
                      {
                        key: 'logout',
                        label: '登出',
                        onClick: () => {
                          handleLogout();
                          setAnchorEl(false);
                        }
                      }
                    ]
                  }}
                  open={anchorEl}
                  onOpenChange={setAnchorEl}
                  trigger={['click']}
                >
                  <Avatar
                    src={user?.avatar_url || defaultAvatar}
                    alt={user?.name || 'User'}
                    size={40}
                    style={{ 
                      cursor: 'pointer',
                      border: '2px solid #fff'
                    }}
                  />
                </Dropdown>
              </>
            ) : (
              <>
                <Link to="/prices" style={{ marginRight: '16px', color: '#ffcc00', display: 'inline-flex', alignItems: 'center' }} title="價格方案">
                  <DollarOutlined style={{ fontSize: '18px', marginRight: 6 }} />
                  <span style={{ fontSize: 14 }}>價格方案</span>
                </Link>
                <Button type="text" onClick={() => setShowLoginDialog(true)} style={{ color: '#ffcc00' }}>
                  登入
                </Button>
              </>
            )}
          </div>
        </div>
        <nav className="parent-nav">
          <div className="parent-nav-wrapper">
            <Link
              to="/top-companies"
              className={`parent-nav-link ${location.pathname === '/top-companies' ? 'active' : ''}`}
            >
              <AppstoreOutlined /> 權值列表
            </Link>
            {/*
            <Link
              to="/business-product"
              className={`parent-nav-link ${isReportSection ? 'active' : ''}`}
            >
              <ProfileOutlined /> 個股研究
            </Link>
            */}

            <Link
              to="/top-momentum"
              className={`parent-nav-link ${location.pathname === '/top-momentum' ? 'active' : ''}`}
            >
              <ThunderboltOutlined /> 動能選股
            </Link>
            <Link
              to="/pages/stock-screener"
              className={`parent-nav-link ${location.pathname === '/pages/stock-screener' ? 'active' : ''}`}
            >
              <RocketOutlined /> 智慧選股
            </Link>
            <a
              href="https://blog.deepbuffett.com/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="parent-nav-link"
            >
              <FileTextOutlined /> 部落格
            </a>
            <Link
              to="/about-me"
              className={`parent-nav-link ${location.pathname === '/about-me' ? 'active' : ''}`}
            >
              <InfoCircleOutlined /> 關於我
            </Link>
          </div>
        </nav>
      </div>

      {/* 公司信息显示区块 */}
      {companyInfo && (isReportSection || location.pathname === '/business-product') && (
        <div className="company-info-bar">
          <div className="company-info-content">
            <div className="company-left">
              <img 
                src={companyInfo.image || '/default-company-icon.png'} 
                alt={`${companyInfo.company_name} logo`}
                className="company-logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-company-icon.png';
                }}
              />
              <div className="company-name">{companyInfo.company_name}</div>
              {companyInfo.symbol && (
                <Nav.Link className="watchlist-nav-link">
                  {isAuthenticated ? (
                    <Button
                      type={isInWatchlist ? "default" : "primary"}
                      icon={isInWatchlist ? <StarFilled /> : <StarOutlined />}
                      onClick={handleAddToWatchlist}
                      disabled={isCheckingWatchlist || isInWatchlist}
                      className="watchlist-btn"
                    >
                      <span className="watchlist-text">{isInWatchlist ? '已收藏' : '收藏'}</span>
                    </Button>
                  ) : (
                    <Button
                      type="default"
                      icon={<StarOutlined />}
                      onClick={() => setShowLoginDialog(true)}
                      className="watchlist-btn"
                    >
                      <span className="watchlist-text">收藏</span>
                    </Button>
                  )}
                </Nav.Link>
              )}
            </div>
            <div className="company-details">
              <span className="sector">產業板塊 : {companyInfo.sector}</span>
              <span className="industry">產業細分 : {companyInfo.industry}</span>
            </div>
          </div>
        </div>
      )}

      {/* Sub Navbar - 只在 Report section 顯示 */}
      {isReportSection && (
        <Navbar expand="lg" className="sub-navbar show">
          <div className="sub-navbar-container">
            <Nav className="me-auto">
              
              <Nav.Link 
                onClick={() => handleSubNavClick('/maininfo')}
                className={location.pathname === '/maininfo' ? 'active' : ''}
              >
                <BarChartOutlined /> 資訊主頁
              </Nav.Link>
              {/* 消息面已整合到資訊主頁，不再单独显示 */}
              {/*
              <Nav.Link 
                onClick={() => handleSubNavClick('/list-news')}
                className={location.pathname === '/list-news' ? 'active' : ''}
              >
                <NotificationOutlined /> 消息面
              </Nav.Link>
              */}
              <Nav.Link 
                onClick={() => handleSubNavClick('/income')}
                className={location.pathname === '/income' ? 'active' : ''}
              >
                <DollarOutlined /> 財務數據
              </Nav.Link>
              <Nav.Link 
                onClick={() => handleSubNavClick('/transcript')}
                className={['/transcript', '/list-10q10k'].includes(location.pathname) ? 'active' : ''}
              >
                <FileTextOutlined /> 財報法說
              </Nav.Link>
            </Nav>
          </div>
        </Navbar>
      )}

      {/* Sub Navbar - 只在 Financial section 顯示 */}
      {isFinancialSection && (
        <div className="financial-sub-navbar">
          <div className="financial-sub-navbar-container">
            <Navbar>
              <Nav className="me-auto">
                <Nav.Link 
                  onClick={() => handleSubNavClick('/income')}
                  className={location.pathname === '/income' ? 'active' : ''}
                >
                  <DollarOutlined /> 損益表
                </Nav.Link>
                <Nav.Link
                  onClick={() => handleSubNavClick('/balance')}
                  className={location.pathname === '/balance' ? 'active' : ''}
                >
                  <BankOutlined /> 資產負債表
                </Nav.Link>
                <Nav.Link 
                  onClick={() => handleSubNavClick('/cashflow')}
                  className={location.pathname === '/cashflow' ? 'active' : ''}
                >
                  <FundOutlined /> 現金流量表
                </Nav.Link>
                <Nav.Link 
                  onClick={() => handleSubNavClick('/keymetric')}
                  className={location.pathname === '/keymetric' ? 'active' : ''}
                >
                  <BarChartOutlined /> 關鍵指標
                </Nav.Link>
                <Nav.Link 
                  onClick={() => handleSubNavClick('/estimate')}
                  className={location.pathname === '/estimate' ? 'active' : ''}
                >
                  <FundOutlined /> 業績預估
                </Nav.Link>
              </Nav>
            </Navbar>
          </div>
        </div>
      )}

      <LoginDialog 
        open={showLoginDialog} 
        onClose={() => setShowLoginDialog(false)} 
        openRegister={() => {
          setShowLoginDialog(false); 
          setShowRegisterDialog(true);
        }}
        isLimitReached={false}
      />
      <RegisterDialog 
        open={showRegisterDialog} 
        onClose={() => setShowRegisterDialog(false)} 
      />

      <style jsx>{`
        .navigation {
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          overflow-y: visible;
          box-sizing: border-box;
          position: relative;
        }
        
        .promo-banner {
          background-color: #d32f2f;
          color: #ffffff;
          text-align: center;
          padding: 8px 16px;
          font-weight: 700;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }
        .promo-button {
          background-color: #ffffff !important;
          color: #d32f2f !important;
          border: 1px solid #ffffff !important;
          font-weight: 700 !important;
          height: 28px !important;
          padding: 0 12px !important;
          font-size: 12px !important;
        }
        .promo-button:hover {
          background-color: #f5f5f5 !important;
          color: #b71c1c !important;
          border-color: #f5f5f5 !important;
        }
        .parent-navbar {
          background-color: #001529;
          color: white;
          padding: 0;
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          overflow-y: visible;
          box-sizing: border-box;
          position: relative;
          z-index: 1000;
        }
        .parent-navbar-container {
          display: flex;
          align-items: center;
          padding: 0.5rem 20px;
          justify-content: space-between;
          max-width: 1600px;
          width: 100%;
          margin: 0 auto;
          box-sizing: border-box;
          overflow-x: hidden;
          overflow-y: visible;
        }
        .navbar-logo img {
          height: 40px;
        }
        .navbar-search-container {
          flex: 1;
          max-width: 400px;
          margin: 0 1rem;
          position: relative;
          z-index: 10001;
        }
        .parent-nav {
          display: flex;
          background-color: #001529;
          padding: 0;
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
          flex-wrap: nowrap;
          width: 100%;
          flex-direction: row;
          justify-content: center;
        }
        .parent-nav-wrapper {
          display: flex;
          width: 100%;
          max-width: 1600px;
          margin: 0 auto;
          padding: 0 20px;
          justify-content: center;
        }
        .parent-nav-link {
          color: #fff;
          padding: 0.8rem 1.5rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          font-size: 15px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .parent-nav-link:hover {
          color: #1890ff;
        }
        .parent-nav-link.active {
          color: #1890ff;
          font-weight: bold;
        }
        .parent-nav-link .anticon {
          margin-right: 4px;
        }
        .auth-section {
          display: flex;
          align-items: center;
        }
        .watchlist-btn {
          height: 20px;
          font-size: 10px;
        }
        .sub-navbar {
          background-color: #fff;
          padding: 0;
          border-bottom: 2px solid #000;
          width: 100%;
          display: flex;
          justify-content: center;
          position: relative;
          z-index: 900;
        }
        .sub-navbar-container {
          max-width: 1600px;
          width: 100%;
          padding: 0 20px;
          margin: 0 auto;
          display: flex;
        }
        .sub-navbar .nav {
          padding: 0;
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
        }
        .sub-navbar .nav-link {
          padding: 8px 12px;
          color: #666;
          font-size: 16px;
        }
        .sub-navbar .nav-link:hover,
        .sub-navbar .nav-link.active {
          color: #1890ff;
          background-color: transparent;
        }
        .sub-navbar .watchlist-nav-link {
          padding: 0;
          margin-right: 10px;
        }
        .watchlist-btn {
          height: 20px;
          font-size: 10px;
        }
        .watchlist-text {
          margin-left: 1px;
        }
        .auth-section .anticon {
          font-size: 12px;
        }
        .company-info-bar {
          background-color: #fff;
          padding: 8px 0;
          border-bottom: 1px solid #e8e8e8;
          width: 100%;
          position: relative;
          z-index: 900;
        }
        
        .company-info-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          max-width: 1600px;
          width: 100%;
          margin: 0 auto;
        }
        
        .company-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .company-logo {
          width: 40px;
          height: 40px;
          border-radius: 4px;
          object-fit: contain;
          background-color: white;
          padding: 2px;
        }
        
        .company-name {
          font-size: 25px;
          font-weight: bold;
          color: #333;
        }
        
        .company-details {
          display: flex;
          gap: 20px;
          color: #666;
          font-size: 9px !important;
        }
        
        .company-details span {
          font-size: 14px;
        }
        
        .company-details .website {
          color: #1890ff;
          text-decoration: none;
          font-size: 14px;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .company-details .website:hover {
          text-decoration: underline;
        }
        
        @media screen and (max-width: 1600px) {
          .parent-navbar-container {
            max-width: 100%;
          }
          
          .company-info-content {
            max-width: 100%;
          }
          
          .sub-navbar-container {
            max-width: 100%;
          }
          
          .parent-nav-wrapper {
            max-width: 100%;
          }
          
          .financial-sub-navbar-container {
            max-width: 100%;
          }
        }

        @media screen and (max-width: 768px) {
          .parent-navbar-container {
            padding: 0.3rem 15px;
            width: 100%;
          }
          
          .company-info-content {
            padding: 0 15px;
            width: 100%;
          }
          
          .sub-navbar-container {
            padding: 0 15px;
            width: 100%;
          }
          
          .parent-nav-wrapper {
            padding: 0 15px;
            width: 100%;
          }
          
          .financial-sub-navbar-container {
            padding: 0 15px;
            width: 100%;
          }
          .navbar-logo img {
            height: 32px;
          }
          .navbar-search-container {
            margin: 0 0.5rem;
          }
          .parent-nav {
            padding: 0 1rem;
            overflow-x: visible;
            justify-content: space-between;
          }
          .parent-nav-link {
            padding: 0.8rem 1rem;
            font-size: 14px;
            flex: 1;
            justify-content: center;
          }
          .parent-nav-link .anticon {
            margin-right: 4px;
          }
          .sub-navbar .nav-link {
            font-size: 14px !important;
          }
          .nav-link-custom {
            font-size: 14px !important;
          }
        }

        @media screen and (max-width: 480px) {
          .parent-navbar-container {
            padding: 0.2rem 10px;
            width: 100%;
          }
          
          .company-info-content {
            padding: 0 10px;
            width: 100%;
          }
          
          .sub-navbar-container {
            padding: 0 10px;
            width: 100%;
          }
          
          .parent-nav-wrapper {
            padding: 0 10px;
            width: 100%;
          }
          
          .financial-sub-navbar-container {
            padding: 0 10px;
            width: 100%;
          }
          .promo-banner {
            font-size: 12px;
            padding: 6px 8px;
            flex-direction: column;
            gap: 8px;
          }
          .promo-button {
            height: 24px !important;
            font-size: 11px !important;
            padding: 0 8px !important;
          }
          .navbar-logo img {
            height: 28px;
          }
          .navbar-search-container {
            margin: 0 0.3rem;
          }
          .parent-nav {
            padding: 0 0.3rem;
            flex-direction: row;
            display: flex;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            justify-content: flex-start;
            gap: 10px;
          }
          .parent-nav-link {
            padding: 0.4rem 0.2rem;
            font-size: 13px;
            flex-shrink: 0;
            width: auto;
          }
          .parent-nav-link .anticon {
            margin-right: 1px;
            font-size: 13px;
          }
          .watchlist-btn {
            height: 24px;
            padding: 0 6px;
            font-size: 11px;
          }
          .watchlist-text {
            margin-left: 1px;
          }
          .auth-section .anticon {
            font-size: 12px;
          }
          .company-details {
            display: none !important;
          }
          .financial-sub-navbar .nav {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            white-space: nowrap !important;
            gap: 10px;
            width: 100% !important;
            padding: 0 10px !important;
          }
          .financial-sub-navbar .nav-link {
            font-size: 14px !important;
          }
          .nav-dropdown .nav-link,
          .nav-link-custom {
            font-size: 14px !important;
            padding: 6px 8px;
          }
          .sub-navbar .nav-link {
            font-size: 14px !important;
          }
          .dropdown-menu,
          .dropdown-menu-right {
            min-width: 140px;
            font-size: 12px;
            padding: 6px 0;
            z-index: 2147483647 !important; /* 使用最大可能的z-index值 */
            position: fixed !important;
            background-color: #fff !important;
            border: 1px solid #ddd !important;
            border-radius: 6px !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.3) !important;
            border-top: 3px solid #1890ff !important;
            pointer-events: auto !important;
            visibility: visible !important;
            opacity: 1 !important;
            /* 強制手機版顯示在最上層 */
            transform: translateZ(0) !important;
            -webkit-transform: translateZ(0) !important;
            will-change: transform !important;
          }

          .dropdown-item {
            padding: 8px 16px;
            font-size: 14px;
            margin: 1px 4px;
          }
          .dropdown-item .anticon {
            margin-right: 6px;
            font-size: 14px;
          }
        }

        .financial-sub-navbar {
          background: none;
          border: none;
          box-shadow: none;
          margin: 0;
          padding: 0;
          margin-top: 18px;
          width: 100%;
          display: flex;
          justify-content: center;
        }
        
        .financial-sub-navbar-container {
          max-width: 1600px;
          width: 100%;
          padding: 0 20px;
          margin: 0 auto;
          display: flex;
          box-sizing: border-box;
        }
        
        .financial-sub-navbar-container .navbar {
          width: 100%;
          padding: 0;
          background: none;
        }
        
        .financial-sub-navbar .nav {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          gap: 0;
          overflow: hidden;
          border-radius: 24px;
          border: 2px solid #222;
          background: #fff;
          padding: 0;
          width: 100%;
          box-sizing: border-box;
        }
        .financial-sub-navbar .nav-link {
          border: 1px solid #222;
          background: #fff;
          color: #444;
          font-size: 14px;
          padding: 8px 24px;
          border-radius: 0;
          transition: background 0.2s, color 0.2s;
          box-shadow: none;
          position: relative;
          font-weight: 500;
          margin-left: -1px;
        }
        .financial-sub-navbar .nav-link:first-child {
          border-top-left-radius: 24px;
          border-bottom-left-radius: 24px;
          margin-left: 0;
        }
        .financial-sub-navbar .nav-link:last-child {
          border-top-right-radius: 24px;
          border-bottom-right-radius: 24px;
        }
        .financial-sub-navbar .nav-link.active {
          background: #222;
          color: #fff;
          z-index: 1;
        }
        .financial-sub-navbar .anticon {
          font-size: 16px;
          margin-right: 6px;
        }
        .sub-navbar.show .nav-link:first-child {
          margin-left: 0;
        }
        .nav-dropdown {
          position: relative;
          display: inline-block;
          z-index: 999999999;
        }
        
        /* 手機版下拉選單觸控優化 */
        @media (max-width: 768px) {
          .latest-report-dropdown {
            z-index: 2147483647 !important;
          }
          
          .latest-report-dropdown .ant-dropdown-menu {
            background-color: #fff !important;
            border: 1px solid #ddd !important;
            border-radius: 6px !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.3) !important;
            border-top: 3px solid #1890ff !important;
            min-width: 160px !important;
            padding: 8px 0 !important;
            margin-top: 0 !important; /* 移除頂部間距 */
            /* 手機版強制顯示在最上層 */
            transform: translateZ(0) !important;
            -webkit-transform: translateZ(0) !important;
            will-change: transform !important;
            /* 額外的手機版優化 */
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior: contain !important;
          }
          
          /* 手機版移除Dropdown的默認間距 */
          .latest-report-dropdown .ant-dropdown {
            margin-top: 0 !important;
          }
          
          .custom-dropdown-menu {
            background-color: #fff !important;
            border: 1px solid #ddd !important;
            border-radius: 6px !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.3) !important;
            border-top: 3px solid #1890ff !important;
            min-width: 160px !important;
            padding: 8px 0 !important;
          }
        }
        .nav-dropdown .nav-link,
        .nav-link-custom {
          padding: 8px 12px;
          color: #666;
          font-size: 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        .nav-dropdown .nav-link:hover,
        .nav-dropdown .nav-link.active,
        .nav-link-custom:hover,
        .nav-link-custom.active {
          color: #1890ff;
          background-color: transparent;
        }
        .nav-link-custom .anticon {
          margin-right: 6px;
        }
        /* Ant Design Dropdown 樣式覆蓋 */
        .latest-report-dropdown {
          z-index: 2147483647 !important;
        }
        
        .latest-report-dropdown .ant-dropdown-menu {
          background-color: #fff !important;
          border: 1px solid #ddd !important;
          border-radius: 6px !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3) !important;
          border-top: 3px solid #1890ff !important;
          min-width: 160px !important;
          padding: 8px 0 !important;
          margin-top: 0 !important; /* 移除頂部間距 */
        }
        
        /* 移除Dropdown的默認間距 */
        .latest-report-dropdown .ant-dropdown {
          margin-top: 0 !important;
        }
        
        .custom-dropdown-menu {
          background-color: #fff;
          border: 1px solid #ddd;
          border-radius: 6px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          border-top: 3px solid #1890ff;
          min-width: 160px;
          padding: 8px 0;
        }

        .dropdown-item {
          padding: 12px 20px;
          color: #666;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          white-space: nowrap;
          transition: all 0.2s ease;
          border-radius: 4px;
          margin: 2px 8px;
        }
        .dropdown-item:hover {
          background-color: #f0f8ff;
          color: #1890ff;
          transform: translateX(2px);
        }
        .dropdown-item.active {
          color: #1890ff;
          background-color: #e6f7ff;
          font-weight: 500;
        }
        .dropdown-item .anticon {
          margin-right: 8px;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
}

export default Navigation; 

